import './StaffReportsView.css'

import { useEffect, useMemo } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'

import { staff } from '../../../store'

import { Button } from '../../../components/Button'
import { DataTable } from '../../../components/DataTable'
import { EmptyState } from '../../../components/EmptyState'
import { Tag } from '../../../components/Tag'

export function StaffReportsView() {
  const session = useAtomValue(staff.staffSessionAtom)
  const reports = useAtomValue(staff.staffReportsAtom)
  const isLoading = useAtomValue(staff.staffReportsIsLoadingAtom)
  const error = useAtomValue(staff.staffReportsErrorAtom)
  const actionLoading = useAtomValue(staff.staffActionIsLoadingAtom)

  const fetchReports = useSetAtom(staff.fetchStaffReportsAtom)
  const resolveReport = useSetAtom(staff.resolveStaffReportAtom)

  const canRead = staff.hasStaffPermission(session, staff.Permission.ModerationReportsRead)
  const canWrite = staff.hasStaffPermission(session, staff.Permission.ModerationReportsWrite)

  useEffect(() => {
    if (canRead) fetchReports()
  }, [canRead, fetchReports])

  const columns = useMemo(
    () => [
      {
        id: 'reason',
        header: 'Reason',
        render: (report: staff.StaffReport) => (
          <div className="staff-reports__reason">
            <strong>{report.reason}</strong>
            {report.details && <span>{report.details}</span>}
          </div>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        render: (report: staff.StaffReport) => <Tag color="amber">{report.status}</Tag>,
      },
      {
        id: 'reported',
        header: 'Reported user',
        render: (report: staff.StaffReport) => shortId(report.reportedUserId),
      },
      {
        id: 'reporter',
        header: 'Reporter',
        render: (report: staff.StaffReport) => shortId(report.reporterUserId),
      },
      {
        id: 'created',
        header: 'Opened',
        render: (report: staff.StaffReport) => formatDate(report.createdAt),
      },
      {
        id: 'actions',
        header: '',
        render: (report: staff.StaffReport) =>
          canWrite ? (
            <Button
              kind="secondary"
              size="small"
              text={actionLoading ? '…' : 'Resolve'}
              disabled={actionLoading}
              onClick={() => resolveReport(report.id)}
            />
          ) : null,
      },
    ],
    [canWrite, actionLoading, resolveReport],
  )

  if (!canRead) {
    return (
      <EmptyState
        title="No reports access"
        description="Your role does not include moderation.reports.read."
      />
    )
  }

  return (
    <div className="staff-reports">
      <div className="staff-reports__toolbar">
        <p className="staff-reports__count">
          {isLoading
            ? 'Loading…'
            : `${reports.length} open report${reports.length === 1 ? '' : 's'}`}
        </p>
        <Button
          kind="secondary"
          text={isLoading ? 'Loading…' : 'Refresh'}
          onClick={() => fetchReports()}
          disabled={isLoading}
        />
      </div>

      {error && (
        <EmptyState
          title="Failed to load reports"
          description={error === 'forbidden' ? 'Forbidden for this role.' : 'Request failed.'}
          actions={<Button kind="secondary" text="Retry" onClick={() => fetchReports()} />}
        />
      )}

      {!error && !isLoading && reports.length === 0 && (
        <EmptyState title="No open reports" description="The moderation queue is clear." />
      )}

      {!error && reports.length > 0 && (
        <DataTable columns={columns} rows={reports} getRowKey={(report) => report.id} />
      )}
    </div>
  )
}

function shortId(id: string) {
  return id.slice(-8)
}

function formatDate(value: string) {
  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}
